/** Pure formatting and class-name helpers.
 *
 * Kept out of the component barrel so that file exports components only —
 * which keeps React Fast Refresh working during development.
 */

export function cn(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en', {
    notation: value >= 10000 ? 'compact' : 'standard',
  }).format(value);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Pick the singular or plural label for a count.
 *
 * Small, but "1 pages read" on a dashboard is the kind of detail that makes
 * the rest of the numbers look unattended.
 */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return count === 1 ? singular : (pluralForm ?? `${singular}s`);
}

/**
 * A duration at a precision a person would actually say out loud.
 *
 * Raw seconds from the API arrive as things like 0.111469, which reads as
 * false precision on a dashboard: nobody cares that an audit took 111
 * microseconds more than a tenth of a second.
 */
export function formatDuration(seconds: number): string {
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return 'never';
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}

/**
 * Bands for an agent's self-reported confidence.
 *
 * Shared so the dashboard card and the analysis page cannot disagree about
 * what a given number means — the same 61% reading "moderate" in one place and
 * "well supported" in another would make the figure meaningless.
 */
export function confidenceTone(confidence: number): 'success' | 'brand' | 'warning' {
  if (confidence >= 0.7) return 'success';
  if (confidence >= 0.4) return 'brand';
  return 'warning';
}

export function confidenceBand(confidence: number): string {
  if (confidence >= 0.7) return 'well supported';
  if (confidence >= 0.4) return 'moderate';
  return 'low — verify';
}

/**
 * The bare colour for a severity, for bars and dots rather than badges.
 *
 * Lives here rather than in the component barrel so that file exports
 * components only, which keeps React Fast Refresh working.
 */
export const severityBarClass: Record<string, string> = {
  critical: 'bg-critical',
  error: 'bg-danger',
  warning: 'bg-warning',
  opportunity: 'bg-brand',
  info: 'bg-ink-subtle',
};

/**
 * Compact control styling for filter bars, where several controls sit in one
 * dense row. Shared so the list screens do not drift apart one input at a time.
 */
export const filterInputClass =
  'mt-1 block w-full rounded-lg border border-border-strong bg-surface px-2.5 py-1.5 text-sm ' +
  'text-ink placeholder:text-ink-subtle focus:border-brand focus:ring-1 focus:ring-brand';

/** Shared input styling; a constant rather than a component. */
export const inputClass =
  'block w-full rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-subtle focus:border-brand focus:ring-1 focus:ring-brand disabled:bg-surface-muted';
